const ALLOWED_DAYS = new Set([30, 60, 90, 180]);

function safeDays(n: number): number {
  return ALLOWED_DAYS.has(n) ? n : 90;
}

const CHURN_WINDOW_DAYS = 60;

function engagementCtes(d: number): string {
  return `
WITH
meetings AS (
    SELECT um.company_external_id, um.user_external_id,
      m.external_id AS meeting_id, DATE(m.start_date_utc) AS day
    FROM public.user_meeting um
    JOIN public.meeting m ON m.external_id = um.meeting_external_id
    WHERE m.deleted_date_utc IS NULL
      AND m.category != 'internal_meeting'
      AND m.start_date_utc >= (now() - (${d} * interval '1 day'))
      AND m.start_date_utc <  (now() + interval '1 day')
),
meetings_with_transcript AS (
    SELECT DISTINCT meeting_external_id, user_external_id
    FROM public.meeting_transcription
    WHERE deleted_date_utc IS NULL AND source IS DISTINCT FROM 'demo_transcript'
),
meetings_with_summary AS (
    SELECT DISTINCT meeting_external_id, user_external_id
    FROM public.meeting_synopsis
),
meeting_chats AS (
    SELECT c.company_external_id, c.user_external_id, DATE(cm.created_date_utc) AS day,
      COUNT(*) AS meeting_chats
    FROM public.chat_message cm
    JOIN public.chat c ON c.external_id = cm.chat_external_id
    WHERE cm.role = 'user'
      AND c.meeting_external_id IS NOT NULL AND c.client_external_id IS NULL
      AND cm.created_date_utc >= (now() - (${d} * interval '1 day'))
      AND cm.created_date_utc <  (now() + interval '1 day')
    GROUP BY 1, 2, 3
),
client_chats AS (
    SELECT c.company_external_id, c.user_external_id, DATE(cm.created_date_utc) AS day,
      COUNT(*) AS client_chats
    FROM public.chat_message cm
    JOIN public.chat c ON c.external_id = cm.chat_external_id
    WHERE cm.role = 'user'
      AND c.client_external_id IS NOT NULL AND c.meeting_external_id IS NULL
      AND cm.created_date_utc >= (now() - (${d} * interval '1 day'))
      AND cm.created_date_utc <  (now() + interval '1 day')
    GROUP BY 1, 2, 3
),
per_day AS (
  SELECT m.day, m.company_external_id, co.name AS company_name,
    co.country_code,
    m.user_external_id, u.email AS user_email,
    COUNT(DISTINCT m.meeting_id) AS meetings,
    COUNT(DISTINCT m.meeting_id) FILTER (WHERE mt.meeting_external_id IS NOT NULL) AS transcripts,
    COUNT(DISTINCT m.meeting_id) FILTER (WHERE mt.meeting_external_id IS NOT NULL AND ms.meeting_external_id IS NOT NULL) AS summaries,
    COALESCE(mc.meeting_chats, 0) AS meeting_chats,
    COALESCE(cc.client_chats, 0) AS client_chats
  FROM meetings m
  JOIN public.companies co ON co.external_id = m.company_external_id
  JOIN public.users u ON u.external_id = m.user_external_id
  LEFT JOIN meetings_with_transcript mt ON mt.meeting_external_id = m.meeting_id AND mt.user_external_id = m.user_external_id
  LEFT JOIN meetings_with_summary ms ON ms.meeting_external_id = m.meeting_id AND ms.user_external_id = m.user_external_id
  LEFT JOIN meeting_chats mc ON mc.company_external_id = m.company_external_id AND mc.user_external_id = m.user_external_id AND mc.day = m.day
  LEFT JOIN client_chats cc ON cc.company_external_id = m.company_external_id AND cc.user_external_id = m.user_external_id AND cc.day = m.day
  WHERE u.email NOT LIKE '%@gomarloo.com'
  GROUP BY m.day, m.company_external_id, co.name, co.country_code, m.user_external_id, u.email, mc.meeting_chats, cc.client_chats
)`;
}

export function buildDailyTotalsSql(rangeDays: number): string {
  const d = Math.max(safeDays(rangeDays), CHURN_WINDOW_DAYS);
  return `${engagementCtes(d)}
SELECT day,
  SUM(meetings)::int       AS meetings,
  SUM(transcripts)::int    AS transcripts,
  SUM(summaries)::int      AS summaries,
  SUM(meeting_chats)::int  AS ask_marloo_meeting_chats,
  SUM(client_chats)::int   AS ask_marloo_client_chats
FROM per_day
GROUP BY day
ORDER BY day`;
}

export function buildUserChurnSql(): string {
  return `${engagementCtes(CHURN_WINDOW_DAYS)}
SELECT
  company_external_id,
  company_name,
  country_code,
  user_external_id,
  user_email,
  COALESCE(SUM(meetings)       FILTER (WHERE day >= current_date - interval '28 days'), 0)::int AS meetings_r28,
  COALESCE(SUM(transcripts)    FILTER (WHERE day >= current_date - interval '28 days'), 0)::int AS transcripts_r28,
  COALESCE(SUM(summaries)      FILTER (WHERE day >= current_date - interval '28 days'), 0)::int AS summaries_r28,
  COALESCE(SUM(meeting_chats)  FILTER (WHERE day >= current_date - interval '28 days'), 0)::int AS meeting_chats_r28,
  COALESCE(SUM(client_chats)   FILTER (WHERE day >= current_date - interval '28 days'), 0)::int AS client_chats_r28,
  COALESCE(SUM(meetings)       FILTER (WHERE day <  current_date - interval '28 days' AND day >= current_date - interval '56 days'), 0)::int AS meetings_p28,
  COALESCE(SUM(transcripts)    FILTER (WHERE day <  current_date - interval '28 days' AND day >= current_date - interval '56 days'), 0)::int AS transcripts_p28,
  COALESCE(SUM(summaries)      FILTER (WHERE day <  current_date - interval '28 days' AND day >= current_date - interval '56 days'), 0)::int AS summaries_p28,
  COALESCE(SUM(meeting_chats + client_chats) FILTER (WHERE day <  current_date - interval '28 days' AND day >= current_date - interval '56 days'), 0)::int AS chats_p28,
  COALESCE(SUM(meeting_chats + client_chats) FILTER (WHERE day >= current_date - interval '14 days'), 0)::int AS chats_r14,
  COALESCE(SUM(meeting_chats + client_chats) FILTER (WHERE day <  current_date - interval '14 days' AND day >= current_date - interval '28 days'), 0)::int AS chats_p14,
  COALESCE(SUM(meetings) FILTER (WHERE day >= current_date - interval '14 days'), 0)::int AS meetings_r14
FROM per_day
GROUP BY company_external_id, company_name, country_code, user_external_id, user_email`;
}

export function buildCompanySql(rangeDays: number): string {
  const d = safeDays(rangeDays);
  return `
WITH company_active_users AS (
  SELECT
    co.external_id,
    co.name AS company_name,
    co.country_code,
    COUNT(ur.user_external_id) FILTER (WHERE ur.status = 'active') AS active_users
  FROM public.companies co
  LEFT JOIN public.user_roles ur
    ON ur.company_external_id = co.external_id
  GROUP BY co.external_id, co.name, co.country_code
),

canonical_companies AS (
  SELECT DISTINCT ON (lower(company_name))
    external_id,
    company_name,
    country_code,
    active_users
  FROM company_active_users
  ORDER BY lower(company_name), active_users DESC, external_id
),

billing_in_window AS (
  SELECT *
  FROM public.company_billing cb
  WHERE cb.billing_date_utc IS NOT NULL
    AND cb.billing_date_utc >= (current_date - (${d} * interval '1 day'))
    AND cb.billing_date_utc <= current_date
),

agg_all AS (
  SELECT
    company_external_id,
    MAX(billing_plan::text) AS billing_plan_any,
    COALESCE(MAX(billed_quantity), 0) AS billed_quantity_any,
    MAX(stripe_subscription_id) AS stripe_subscription_id_any
  FROM public.company_billing
  GROUP BY company_external_id
),

agg_window AS (
  SELECT
    company_external_id,
    COALESCE(MAX(billed_quantity), 0) AS billed_quantity_window,
    COUNT(*) AS rows_in_window
  FROM billing_in_window
  GROUP BY company_external_id
)

SELECT
  co.external_id                                      AS company_external_id,
  co.company_name,
  co.country_code,
  agg_all.billing_plan_any                            AS billing_plan,
  agg_all.stripe_subscription_id_any                  AS stripe_subscription_id,
  CASE
    WHEN COALESCE(agg_window.rows_in_window, 0) > 0 THEN COALESCE(agg_window.billed_quantity_window, 0)
    WHEN COALESCE(rcc.last_billed_quantity, 0) > 0  THEN COALESCE(rcc.last_billed_quantity, 0)
    WHEN COALESCE(rcc.total_seats, 0) > 0           THEN COALESCE(rcc.total_seats, 0)::integer
    ELSE COALESCE(agg_all.billed_quantity_any, 0)
  END                                                 AS seats_max
FROM canonical_companies co
LEFT JOIN agg_all
  ON agg_all.company_external_id = co.external_id
LEFT JOIN agg_window
  ON agg_window.company_external_id = co.external_id
LEFT JOIN reporting.company_conversions rcc
  ON rcc.company_external_id = co.external_id
ORDER BY co.company_name
`;
}

export const USER_ROSTER_SQL = `
SELECT DISTINCT
  uav.company_external_id,
  uav.company_name,
  uav.user_external_id,
  uav.email                 AS user_email,
  uav.billing_plan::text    AS billing_plan
FROM views.user_activity_view uav
WHERE uav.company_external_id IS NOT NULL
  AND uav.email NOT LIKE '%@gomarloo.com'
ORDER BY uav.company_name, uav.email
`;

export type DailyTotalRow = {
  day: string;
  meetings: number;
  transcripts: number;
  summaries: number;
  ask_marloo_meeting_chats: number;
  ask_marloo_client_chats: number;
};

export type UserChurnRow = {
  company_external_id: string;
  company_name: string;
  country_code: string | null;
  user_external_id: string;
  user_email: string;
  meetings_r28: number;
  transcripts_r28: number;
  summaries_r28: number;
  meeting_chats_r28: number;
  client_chats_r28: number;
  meetings_p28: number;
  transcripts_p28: number;
  summaries_p28: number;
  chats_p28: number;
  chats_r14: number;
  chats_p14: number;
  meetings_r14: number;
};

export type CompanyRow = {
  company_external_id: string;
  company_name: string;
  country_code: string | null;
  billing_plan: string | null;
  stripe_subscription_id: string | null;
  seats_max: number;
};

export type UserRosterRow = {
  company_external_id: string;
  company_name: string;
  user_external_id: string;
  user_email: string;
  billing_plan: string | null;
};

function indexer(columns: string[]) {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  return (row: unknown[], k: string): unknown =>
    idx[k] !== undefined ? row[idx[k] as number] : undefined;
}

const str = (v: unknown): string => (v == null ? "" : String(v));
const strNull = (v: unknown): string | null => (v == null ? null : String(v));
const num = (v: unknown): number => {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function mapDailyTotalRows(columns: string[], rows: unknown[][]): DailyTotalRow[] {
  const get = indexer(columns);
  return rows.map((r) => ({
    day:                      str(get(r, "day")).slice(0, 10),
    meetings:                 num(get(r, "meetings")),
    transcripts:              num(get(r, "transcripts")),
    summaries:                num(get(r, "summaries")),
    ask_marloo_meeting_chats: num(get(r, "ask_marloo_meeting_chats")),
    ask_marloo_client_chats:  num(get(r, "ask_marloo_client_chats")),
  }));
}

export function mapUserChurnRows(columns: string[], rows: unknown[][]): UserChurnRow[] {
  const get = indexer(columns);
  return rows.map((r) => ({
    company_external_id: str(get(r, "company_external_id")),
    company_name:        str(get(r, "company_name")),
    country_code:        strNull(get(r, "country_code")),
    user_external_id:    str(get(r, "user_external_id")),
    user_email:          str(get(r, "user_email")),
    meetings_r28:        num(get(r, "meetings_r28")),
    transcripts_r28:     num(get(r, "transcripts_r28")),
    summaries_r28:       num(get(r, "summaries_r28")),
    meeting_chats_r28:   num(get(r, "meeting_chats_r28")),
    client_chats_r28:    num(get(r, "client_chats_r28")),
    meetings_p28:        num(get(r, "meetings_p28")),
    transcripts_p28:     num(get(r, "transcripts_p28")),
    summaries_p28:       num(get(r, "summaries_p28")),
    chats_p28:           num(get(r, "chats_p28")),
    chats_r14:           num(get(r, "chats_r14")),
    chats_p14:           num(get(r, "chats_p14")),
    meetings_r14:        num(get(r, "meetings_r14")),
  }));
}

export function mapCompanyRows(columns: string[], rows: unknown[][]): CompanyRow[] {
  const get = indexer(columns);
  return rows.map((r) => ({
    company_external_id:     str(get(r, "company_external_id")),
    company_name:            str(get(r, "company_name")),
    country_code:            strNull(get(r, "country_code")),
    billing_plan:            strNull(get(r, "billing_plan")),
    stripe_subscription_id:  strNull(get(r, "stripe_subscription_id")),
    seats_max:               num(get(r, "seats_max")),
  }));
}

export function mapUserRosterRows(columns: string[], rows: unknown[][]): UserRosterRow[] {
  const get = indexer(columns);
  return rows.map((r) => ({
    company_external_id:  str(get(r, "company_external_id")),
    company_name:         str(get(r, "company_name")),
    user_external_id:     str(get(r, "user_external_id")),
    user_email:           str(get(r, "user_email")),
    billing_plan:         strNull(get(r, "billing_plan")),
  }));
}
